import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Dialog } from '@opendoor/atoms/dialog';
import { Input } from '@opendoor/atoms/input';
import { Text } from '@opendoor/atoms/text';
import type { SavedSearchFieldsFragment, SavedSearchInput } from '__generated__/athena/graphql';

import { toPropertySearchQuery, toSavedSearchInput } from './mapping';
import type { SavedSearchMutationResult } from './useSavedSearches';

export interface RenameSavedSearchDialogProps {
  savedSearch: SavedSearchFieldsFragment;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Renames via the host's shared `useSavedSearches` instance so the list the
   * dropdown renders refetches and updates in place (a separate instance would
   * only reflect the change on reload).
   */
  onRename: (id: string, input: SavedSearchInput) => Promise<SavedSearchMutationResult>;
}

const SAVE_SEARCH_NAME_MAX_LENGTH = 80;
const GENERIC_ERROR = 'Failed to rename search. Please try again.';

export const RenameSavedSearchDialog = ({
  savedSearch,
  isOpen,
  onClose,
  onRename,
}: RenameSavedSearchDialogProps) => {
  const [name, setName] = useState(savedSearch.name);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const nameInputId = useId();

  // Reset name + error whenever a different saved search is opened for rename.
  useEffect(() => {
    if (isOpen) {
      setName(savedSearch.name);
      setErrorMessage(null);
      queueMicrotask(() => {
        inputRef.current?.focus();
      });
    }
  }, [isOpen, savedSearch.name, savedSearch.id]);

  const handleClose = useCallback(() => {
    if (submitting) {
      return;
    }
    onClose();
  }, [submitting, onClose]);

  const handleSubmit = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName || submitting) {
      return;
    }
    if (trimmedName === savedSearch.name) {
      onClose();
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    // Round-trip the full record through the mapping layer so unmapped
    // fields (max bed/bath, opendoor-owned, FHA, finite HOA cap) survive
    // the update. `toSavedSearchInput` receives `savedSearch` as the
    // preservation source.
    const query = toPropertySearchQuery(savedSearch);
    const input = toSavedSearchInput(query, trimmedName, savedSearch);
    const result = await onRename(savedSearch.id, input);
    setSubmitting(false);
    if (result.success) {
      onClose();
      return;
    }
    setErrorMessage(result.errorMessage ?? GENERIC_ERROR);
  }, [name, savedSearch, submitting, onRename, onClose]);

  const trimmedName = name.trim();
  const canSubmit = !submitting && trimmedName.length > 0;

  let submitLabel = 'Save';
  if (submitting) {
    submitLabel = 'Saving…';
  }

  return (
    <Dialog.Root
      role="dialog"
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
      disablePointerDismissal={submitting}
    >
      <Dialog title="Rename saved search">
        <Dialog.Description className="text-body-md text-od-content-secondary">
          Update the label for this saved search.
        </Dialog.Description>
        <Col className="pt-4 pb-2">
          <Col className="gap-1.5">
            <Text
              as="label"
              htmlFor={nameInputId}
              className="text-label-sm text-od-content-primary"
            >
              Search name
            </Text>
            <Input
              ref={inputRef}
              id={nameInputId}
              size="base"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={SAVE_SEARCH_NAME_MAX_LENGTH}
              analyticsName="rename-saved-search-name-input"
              placeholder="My saved search"
            />
          </Col>
          {errorMessage && (
            <Text as="p" role="alert" className="pt-2 text-body-sm text-od-content-error">
              {errorMessage}
            </Text>
          )}
        </Col>
        <Row className="justify-end gap-2 pt-6">
          <Button
            variant="secondary"
            size="md"
            aria-label="Cancel"
            analyticsName="rename-saved-search-cancel"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            aria-label={submitLabel}
            analyticsName="rename-saved-search-submit"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={submitting}
          >
            {submitLabel}
          </Button>
        </Row>
      </Dialog>
    </Dialog.Root>
  );
};
